import { ErrorModal, MesfModal, useShiftNavigator } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, get, useForm } from "react-hook-form";
import { HeatFormData, INITIAL_VALUES } from "../../models/Initial-values";
import { upsertHeat } from "../../repositories/heatsRepository";
import { getHeat } from "@/pages/meltshop/repositories/HeatRepository";
import { Heat } from "@/pages/meltshop/models/Heat";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { LadleSelector } from "@/shared/selectors/LadleSelector";

type Props = {
  heatIdForEdit: number | null;
  show: boolean;
  onHide: (shouldUpdate: boolean) => void;
  heatsByShift: Heat[];
};

export const EditHeatModal = ({
  heatIdForEdit,
  show,
  onHide,
  heatsByShift,
}: Props) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { shiftInfo } = useShiftNavigator();

  const { control, handleSubmit, reset } = useForm<any>({
    defaultValues: INITIAL_VALUES,
  });

  const onSubmit: SubmitHandler<HeatFormData> = async (data) => {
    setIsSubmitLoading(true);

    try {
      if (heatsByShift && heatsByShift.length > 0) {
        const heatNameExists = heatsByShift.some((heat) => {
          const existingHeatName = get(heat, "HeatName", null);
          const currentHeatId = get(heat, "HeatId", null);

          if (heatIdForEdit && currentHeatId === heatIdForEdit) {
            return false;
          }

          return (
            existingHeatName &&
            existingHeatName.toLowerCase() === data.HeatName.toLowerCase()
          );
        });

        if (heatNameExists) {
          setError("A Heat with this name already exists");
          return;
        }
      }

      let res;
      if (heatIdForEdit) {
        res = await upsertHeat(heatIdForEdit, data);
      } else {
        res = await upsertHeat(null, {
          ...data,
          ShiftId: get(shiftInfo, "CurrentShiftId", null),
        });
      }

      if (res.ok) {
        setNotificationMessage(
          heatIdForEdit
            ? "The heat was updated successfully"
            : "The heat was created successfully",
        );
        onHide(true);
      } else {
        setError(res.message);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !heatIdForEdit) {
      return;
    }

    (async () => {
      const resp = await getHeat(heatIdForEdit);

      if (resp.ok) {
        const heat = get(resp, "data.tables[0].rows[0]", null);

        if (heat) {
          reset({
            HeatId: heat.HeatId,
            HeatName: heat.HeatName,
            ShiftId: get(shiftInfo, "CurrentShiftId", null),
            GradeId: heat.GradeId,
            SteelWeight: heat.SteelWeight
              ? parseFloat(heat.SteelWeight).toFixed(1)
              : "",
            LadleId: heat.LadleId,
            ProductionYear: heat.ProductionYear,
          });
        } else {
          onHide(true);
          setError("Error, heat not found. Try again");
        }
      } else {
        setError(resp.message);
      }
    })();
  }, [show, heatIdForEdit]);

  return (
    <>
      <MesfModal
        open={show}
        handleClose={() => {
          onHide(true);
        }}
        id="editHeat"
        title="Update a heat"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content style={{ padding: "15px 30px" }}>
            <Grid container spacing={3}>
              <Grid item md={8} sm={12} xs={12}>
                <Controller
                  name="HeatName"
                  rules={{
                    required: "Name is required",
                    maxLength: {
                      value: 6,
                      message: "Name cannot be longer than 6 characters",
                    },
                  }}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        {...field}
                        label="Name"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        margin="dense"
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4} sm={12} md={4}>
                <Controller
                  name="ProductionYear"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        disabled
                        value={field.value || ""}
                        onChange={field.onChange}
                        label="Year"
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        margin="dense"
                      />
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="GradeId"
                  control={control}
                  render={({ field }) => (
                    <GradeSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="SteelWeight"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value || ""}
                      onChange={field.onChange}
                      label="Steel Weight"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="LadleId"
                  control={control}
                  render={({ field }) => (
                    <LadleSelector
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "2rem" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item md={3} xs={12} style={{ margin: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => {
                    onHide(false);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item md={3} xs={12} style={{ margin: 0 }}>
                <Button
                  fullWidth
                  startIcon={
                    isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
                  }
                  disabled={isSubmitLoading}
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </MesfModal.Actions>
        </form>
      </MesfModal>

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal onHide={() => setError("")} error={error} />
    </>
  );
};
